import { m } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  FileSearch,
  ClipboardCheck,
  Scale,
  GraduationCap,
  Award,
  Building2,
  Gavel,
  AlertTriangle,
  CalendarClock,
  ShieldCheck,
  Quote,
  Star,
} from "lucide-react";
import { Receipt, Users } from "lucide-react";
import SampleReportModal from "@/components/SampleReportModal";
import { Seo } from "@/components/Seo";
import { optImg, imgSrcSet } from "@/lib/img";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HERO_IMG =
  "https://images.unsplash.com/photo-1646503828437-649ce8ef076f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxsb25kb24lMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBleHRlcmlvcnxlbnwwfHx8fDE3ODI2Mzc1ODd8MA&ixlib=rb-4.1.0&q=85";

const HOME_CONSULT_IMG =
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBtZWV0aW5nfGVufDB8fHx8MTc4MjYzNzU4N3ww&ixlib=rb-4.1.0&q=85";

const services = [
  {
    icon: FileSearch,
    title: "HHSRS Hazard Reports",
    desc: "Professional, court-ready Housing Health & Safety Rating System (HHSRS) hazard assessments across all 21 hazard categories — prepared in line with the Housing Act 2004, the 2026 HHSRS reforms and the Renters' Rights Act 2025.",
    span: "md:col-span-12",
    flagship: true,
  },
  {
    icon: ClipboardCheck,
    title: "Compliance Audits & Advisory",
    desc: "Property compliance audits, remedial action plans and ongoing advisory to keep landlords and managing agents legal and inspection-ready.",
    span: "md:col-span-6",
  },
  {
    icon: Receipt,
    title: "Leasehold & Service Charges",
    desc: "Specialist leasehold support covering service charge disputes, major works bills and lease renewals.",
    span: "md:col-span-6",
  },
  {
    icon: Users,
    title: "Private Tenant Support",
    desc: "Helping private renters where landlords fall short — Rent Repayment Orders, unaddressed HHSRS hazards and disrepair.",
    span: "md:col-span-4",
  },
  {
    icon: Gavel,
    title: "Possession & Court Support",
    desc: "Specialist help with rent possession cases — court forms, money judgment claims and landlord & tenant law.",
    span: "md:col-span-4",
  },
  {
    icon: Scale,
    title: "Renters' Rights Act Readiness",
    desc: "Practical guidance on the 2025 reforms — Section 21 abolition, periodic tenancies and the new Section 8 grounds.",
    span: "md:col-span-4",
  },
  {
    icon: Building2,
    title: "Landlord & Agent Support",
    desc: "Hands-on housing standards support for private landlords and managing agents, drawing on 25+ years inside UK housing.",
    span: "md:col-span-6",
  },
  {
    icon: GraduationCap,
    title: "Training & CPD",
    desc: "Bespoke training for landlords, agents and housing teams on HHSRS, housing standards and regulatory compliance.",
    span: "md:col-span-6",
  },
];

const serviceAnchors = [
  "hhsrs-reports",
  "compliance-audits",
  "leasehold",
  "tenant-support",
  "possession-court",
  "renters-rights-act",
  "landlord-agent-support",
  "training",
];

const expertise = [
  {
    icon: Award,
    title: "HHSRS Practitioner",
    detail: "HHSRS-qualified",
    desc: "Qualified to assess housing hazards under the HHSRS framework, with extensive experience across Central London.",
  },
  {
    icon: GraduationCap,
    title: "CIH Qualified",
    detail: "PG Diploma · LSBU",
    desc: "Postgraduate Diploma in Housing from London South Bank University and CIH membership.",
  },
  {
    icon: Scale,
    title: "Regulatory Compliance",
    detail: "Housing Act 2004 · EPA 1990",
    desc: "Deep working knowledge of the Housing Act 2004 and the Environmental Protection Act 1990.",
  },
  {
    icon: Building2,
    title: "Local Authority Experience",
    detail: "25+ Years · Local Authority",
    desc: "Over 25 years in UK housing, including senior local authority roles across the public sector.",
  },
];

const stats = [
  { value: "25+", label: "Years in UK housing" },
  { value: "21", label: "HHSRS hazard categories" },
  { value: "2004", label: "Housing Act specialists" },
  { value: "2025", label: "Renters' Rights Act ready" },
];

const news = [
  {
    icon: Gavel,
    tag: "In force · 1 May 2026",
    title: "Section 21 'no-fault' evictions abolished",
    desc: "Landlords can no longer serve Section 21 notices. All possession claims now require a valid Section 8 ground and a court hearing.",
  },
  {
    icon: CalendarClock,
    tag: "Renters' Rights Act 2025",
    title: "Assured Periodic Tenancies replace ASTs",
    desc: "Fixed-term ASTs have converted to rolling periodic tenancies, giving tenants greater security of tenure.",
  },
  {
    icon: AlertTriangle,
    tag: "Compliance",
    title: "Stronger enforcement of housing standards",
    desc: "With the Decent Homes Standard and Awaab's Law extending to the private sector, robust HHSRS evidence matters more than ever.",
  },
];

const Home = () => {
  return (
    <div data-testid="home-page">
      <Seo
        title="O Law Ventures Ltd | Housing Standards Compliance & HHSRS Reports"
        description="Expert HHSRS hazard reports and housing standards compliance for UK landlords, agents and tenants. Court-ready assessments across all 21 hazards, updated for the Renters' Rights Act 2025."
      />
      {/* HERO */}
      <section data-testid="hero-section" className="relative h-screen min-h-[640px] flex items-center">
        <div className="absolute inset-0">
          <img
            src={optImg(HERO_IMG, 1920)}
            srcSet={imgSrcSet(HERO_IMG, [640, 828, 1280, 1920])}
            sizes="100vw"
            alt="Modern London architecture and glass buildings against a moody sky, representing UK housing standards and property compliance"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-midnight via-midnight/70 to-midnight/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-midnight via-transparent to-midnight/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full">
          <m.p variants={fadeUp} initial="hidden" animate="visible" custom={0}
            className="text-xs md:text-sm uppercase tracking-[0.3em] text-gold font-semibold mb-6">
            Housing Standards Compliance Specialists
          </m.p>
          <m.h1 variants={fadeUp} initial="hidden" animate="visible" custom={1}
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.95] max-w-4xl text-balance">
            Expert <span className="text-gold">housing safety inspections</span> and landlord and tenant law specialists.
          </m.h1>
          <m.p variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="mt-8 text-lg md:text-xl text-gray-300 font-light max-w-2xl leading-relaxed">
            O Law Ventures helps landlords, managing agents and private tenants
            understand and uphold housing standards under the Housing Act 2004 and the
            Renters' Rights Act 2025 — backed by 25+ years inside UK housing.
          </m.p>
          <m.div variants={fadeUp} initial="hidden" animate="visible" custom={3}
            className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" data-testid="hero-primary-cta"
              className="group inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-midnight font-semibold uppercase tracking-[0.12em] px-8 py-4 transition-colors duration-300">
              Request a hazard report
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/services" data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-gold text-white hover:text-gold font-semibold uppercase tracking-[0.12em] px-8 py-4 transition-colors duration-300">
              Our services
            </Link>
            <Link to="/shop" data-testid="hero-shop-cta"
              className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-gold text-white hover:text-gold font-semibold uppercase tracking-[0.12em] px-8 py-4 transition-colors duration-300">
              Visit the shop
            </Link>
            <SampleReportModal triggerLabel="See a sample report"
              triggerClassName="inline-flex items-center justify-center gap-2 text-gray-300 hover:text-gold font-semibold uppercase tracking-[0.12em] px-8 py-4 transition-colors duration-300 underline underline-offset-4 decoration-white/20 hover:decoration-gold" />
          </m.div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#070A12] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {stats.map((s, i) => (
            <m.div key={s.label} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
              data-testid={`stat-${i}`}>
              <p className="font-heading text-4xl md:text-5xl font-black text-gold tracking-tight">{s.value}</p>
              <p className="mt-2 text-gray-400 font-light text-sm uppercase tracking-wider">{s.label}</p>
            </m.div>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section data-testid="services-bento-grid" className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <div className="max-w-2xl mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-4">What we do</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
            Specialist housing compliance services.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {services.map((s, i) => (
            <m.div key={s.title} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
              data-testid={`service-card-${i}`}
              className={`${s.span} group bg-surface border ${s.flagship ? "border-gold/40" : "border-white/5"} hover:border-gold/50 p-10 transition-all duration-300 hover:-translate-y-1`}>
              {s.flagship && (
                <span className="inline-block text-[0.65rem] uppercase tracking-[0.2em] text-gold font-semibold border border-gold/40 px-3 py-1 mb-5">
                  Flagship Service
                </span>
              )}
              <s.icon className="h-9 w-9 text-gold mb-6" strokeWidth={1.5} />
              <h3 className="font-heading text-2xl font-semibold tracking-tight mb-3">
                <a href={`/services#${serviceAnchors[i]}`} data-testid={`home-service-link-${i}`}
                  className="hover:text-gold transition-colors duration-200">
                  {s.title}
                </a>
              </h3>
              <p className="text-gray-400 font-light leading-relaxed max-w-2xl">{s.desc}</p>
            </m.div>
          ))}
        </div>
      </section>

      {/* SPECIALIST EXPERTISE */}
      <section data-testid="expertise-section" className="bg-[#070A12] border-y border-white/10 py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-4">Specialist expertise</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
              Qualified. Experienced. Trusted.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {expertise.map((e, i) => (
              <m.div key={e.title} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
                data-testid={`expertise-card-${i}`}
                className="group bg-surface border border-white/5 hover:border-gold/50 p-8 transition-all duration-300 hover:-translate-y-1">
                <e.icon className="h-9 w-9 text-gold mb-6" strokeWidth={1.5} />
                <h3 className="font-heading text-xl font-semibold tracking-tight">{e.title}</h3>
                <p className="text-gold text-xs uppercase tracking-[0.15em] mt-1 mb-3">{e.detail}</p>
                <p className="text-gray-400 font-light text-sm leading-relaxed">{e.desc}</p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOUSING SECTOR UPDATES */}
      <section data-testid="news-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-4">Housing sector updates</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
              The Renters' Rights Act 2025 is here.
            </h2>
          </div>
          <Link to="/news" className="text-gold hover:text-gold-hover inline-flex items-center gap-2 uppercase tracking-[0.12em] text-sm font-semibold">
            View all updates <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {news.map((n, i) => (
            <m.div key={n.title} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
              data-testid={`news-card-${i}`}
              className="bg-surface border border-white/5 hover:border-gold/50 p-8 transition-all duration-300 hover:-translate-y-1">
              <n.icon className="h-8 w-8 text-gold mb-5" strokeWidth={1.5} />
              <p className="text-gold text-xs uppercase tracking-[0.15em] mb-3">{n.tag}</p>
              <h3 className="font-heading text-xl font-semibold tracking-tight mb-3">{n.title}</h3>
              <p className="text-gray-400 font-light leading-relaxed">{n.desc}</p>
            </m.div>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[#070A12] border-y border-white/10 py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-16 items-center">
          <m.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-4">Why O Law Ventures</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-8">
              Compliance you can defend.
            </h2>
            <div className="space-y-6">
              {[
                "Reports prepared by a qualified HHSRS practitioner with Central London hazards experience.",
                "25+ years of frontline local authority housing experience.",
                "Clear, evidence-based assessments across all 21 HHSRS hazards.",
                "Up-to-date with the Housing Act 2004 and Renters' Rights Act 2025.",
              ].map((point) => (
                <div key={point} className="flex items-start gap-4">
                  <ShieldCheck className="h-6 w-6 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                  <p className="text-gray-300 font-light text-lg leading-relaxed">{point}</p>
                </div>
              ))}
            </div>
          </m.div>
          <m.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="relative">
            <img
              src={optImg(HOME_CONSULT_IMG, 800)}
              srcSet={imgSrcSet(HOME_CONSULT_IMG, [400, 640, 800, 1040])}
              sizes="(max-width: 1024px) 100vw, 50vw"
              alt="Housing compliance specialist presenting a property inspection report to landlords and agents in a boardroom meeting"
              loading="lazy"
              decoding="async"
              className="w-full h-[520px] object-cover"
            />
            <div className="absolute -bottom-px -left-px h-24 w-24 border-l-2 border-b-2 border-gold" />
            <div className="absolute -top-px -right-px h-24 w-24 border-r-2 border-t-2 border-gold" />
          </m.div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <div className="max-w-2xl mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-4">Client feedback</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
            What our clients say.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            {
              quote:
                "O Law Ventures Ltd has provided housing services to the London Borough of Newham for over five years. Throughout this period, the properties managed have consistently met HHSRS compliance standards, and the company has demonstrated professionalism, reliability, and responsiveness in addressing tenant and maintenance needs. We would have no hesitation in recommending O Law Ventures Ltd to other local authorities or organisations seeking a dependable housing partner.",
              name: "Alex Adesina",
              role: "PRS Officer, London Borough of Newham",
            },
            {
              quote:
                "I had a challenging time with my landlord relating to a possession claim and was referred to O Law Ventures Ltd. They were very helpful in guiding me through the process, completing the necessary forms and signposting to relevant agencies. If it wasn't for their interventions I would have been homeless.",
              name: "Glitzglam Boutique",
              role: "Private tenant — possession claim support",
            },
          ].map((t, i) => (
            <m.div key={t.name} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
              data-testid={`testimonial-card-${i}`}
              className="bg-surface border border-white/5 hover:border-gold/50 p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 flex flex-col">
              <div className="flex gap-1.5 mb-5" role="img" aria-label="Rated 5 out of 5 stars" data-testid={`testimonial-rating-${i}`}>
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} aria-hidden="true" focusable="false" className="h-5 w-5 text-gold fill-current" strokeWidth={1.5} />
                ))}
              </div>
              <Quote className="h-9 w-9 text-gold mb-5" strokeWidth={1.5} aria-hidden="true" focusable="false" />
              <blockquote className="font-serif italic text-lg text-gray-200 leading-relaxed flex-1" data-testid={`testimonial-quote-${i}`}>
                “{t.quote}”
              </blockquote>
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="font-heading text-lg font-semibold" data-testid={`testimonial-author-${i}`}>{t.name}</p>
                <p className="text-gold text-sm uppercase tracking-[0.15em] mt-1">{t.role}</p>
                <p className="text-gray-400 text-xs mt-3">Posted publicly on Google Reviews</p>
              </div>
            </m.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#070A12] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
          <div className="bg-gradient-to-br from-surface to-[#070A12] border border-white/10 p-12 md:p-20 text-center">
            <h2 className="font-heading text-4xl md:text-6xl font-black tracking-tighter max-w-3xl mx-auto text-balance">
              Need a professional <span className="text-gold">HHSRS hazard report?</span>
            </h2>
            <p className="mt-6 text-gray-400 font-light text-lg max-w-xl mx-auto">
              Get in touch for a confidential discussion about your property compliance needs.
            </p>
            <Link to="/contact" data-testid="cta-contact-button"
              className="mt-10 inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-midnight font-semibold uppercase tracking-[0.12em] px-10 py-4 transition-colors duration-300">
              Request a report <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
